import React from "react";
import Carousel, { ButtonGroupProps } from "react-multi-carousel";
import { modeIcon, priceIcon, timeIcon, typeIcon } from "./icons";
import { ProductItem } from "./ProductItem";
import { arrowIcon, chevronRight } from "../../../utils/icons";
import { isAnchor, isFullUrl } from "../../../utils/validators";
import PictureProvider from "../../components/Picture";
import { getStringWithoutHTMLTagsAndEntities } from "../../../utils/formatters";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { LinkWithColor } from "../../../types/Link";
import { useRouter } from "next/router";
import { IDynamicBlockComponent } from "../../dynamicPageBuilder/IDynamicBlockComponent";

type ProductSliderProps = {
  data: Array<ProductItem>;
  background: string | null;
  boxBackground: string | null;
  link: LinkWithColor;
};

const ProductSlider: React.FunctionComponent<IDynamicBlockComponent> = ({
  blockData,
}): React.ReactElement => {
  const {
    background,
    boxBackground,
    link,
    data: productsList,
  } = blockData as ProductSliderProps;
  const { t } = useTranslation();
  const { locale: lang } = useRouter();

  const responsive = {
    desktop: {
      breakpoint: { max: 10000, min: 1280 },
      items: 3,
    },
    tablet: {
      breakpoint: { max: 1280, min: 768 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 768, min: 0 },
      items: 1,
    },
  };

  const isBackgroundWhite = background === "white";
  const styleForContainer = {
    backgroundColor: isBackgroundWhite ? "#f8f8f8" : "#ffffff",
  };
  const styleForCard = {
    backgroundColor: isBackgroundWhite ? "#fff" : "#f8f8f8",
  };

  const ButtonGroup = (props: ButtonGroupProps): React.ReactElement => {
    const { next, previous, carouselState } = props;
    const currentSlide = carouselState?.currentSlide ?? 0;
    const slidesToShow = carouselState?.slidesToShow ?? 0;
    const totalItems = carouselState?.totalItems ?? 0;

    const shouldShowNextButton = totalItems > currentSlide + slidesToShow;
    const shouldShowPrevButton = currentSlide !== 0;

    return (
      <div className="productSlider__buttonsGroup">
        {shouldShowPrevButton && (
          <button
            aria-label="Previous product"
            className="productSlider__buttonsGroup--left"
            onClick={() => previous && previous()}
          >
            <span className="desktopArrow">{arrowIcon}</span>
            <span className="mobileArrow">{chevronRight}</span>
          </button>
        )}
        {shouldShowNextButton && (
          <button
            aria-label="Next product"
            className="productSlider__buttonsGroup--right"
            onClick={() => next && next()}
          >
            <span className="desktopArrow">{arrowIcon}</span>
            <span className="mobileArrow">{chevronRight}</span>
          </button>
        )}
      </div>
    );
  };

  const imgBackgroundColor = boxBackground;

  const carouselProps = {
    swipeable: true,
    draggable: true,
    arrows: false,
    responsive,
    minimumTouchDrag: 100,
    renderButtonGroupOutside: true,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    customButtonGroup: <ButtonGroup />,
  };

  const linkColorStyle = {
    color: link && link.text_color ? link.text_color : "initial",
    fill: link && link.text_color ? link.text_color : "initial",
  };

  return (
    <div className="productSlider__container" style={styleForContainer}>
      <div className="productSlider__header">
        <h2>{t("Popularne kursy")}</h2>
        {!!link &&
          !!link.url &&
          (isFullUrl(link.url) || isAnchor(link.url) ? (
            <a
              className="productSlider__header--link"
              style={linkColorStyle}
              target={link.target}
              rel="noreferrer"
              href={link.url}
            >
              {link.anchor}
              {arrowIcon}
            </a>
          ) : (
            <Link
              className="productSlider__header--link"
              style={linkColorStyle}
              href={`/${lang}${link.url}`}
            >
              {link.anchor}
              {arrowIcon}
            </Link>
          ))}
      </div>
      <Carousel {...carouselProps}>
        {productsList.map((product) => (
          <div
            className="productSlider__slide"
            style={styleForCard}
            key={product.slugs}
          >
            <div className="productSlider__content">
              {product.pictures && (
                <div
                  className="productSlider__content--imgContainer"
                  style={{ backgroundColor: imgBackgroundColor ?? undefined }}
                >
                  {product.bestseller && (
                    <span className="productSlider__content--bestseller">
                      {t("bestseller")}
                    </span>
                  )}
                  <Link passHref href={`/${lang}${product.product_page}`}>
                    <a>
                      <PictureProvider data={product.pictures} />
                    </a>
                  </Link>
                </div>
              )}
              <h3 className="productSlider__slide--title">
                <Link href={`/${lang}${product.product_page}`}>
                  {product.names}
                </Link>
              </h3>
              <p className="productSlider__slide--description">
                {getStringWithoutHTMLTagsAndEntities(product.descriptions)}
              </p>

              <div className="productSlider__slide--attributes">
                <div>
                  <span>{typeIcon}</span>
                  <span>
                    {product.modes.map((mode) => (
                      <span key={mode} className="productSlider__labelWitDot">
                        {mode}
                      </span>
                    ))}
                  </span>
                </div>
                <div>
                  <span>{modeIcon}</span>
                  <span>
                    {product.learningForms.map((learningForm) => (
                      <span
                        key={learningForm}
                        className="productSlider__labelWitDot"
                      >
                        {learningForm}
                      </span>
                    ))}
                  </span>
                </div>
                <div>
                  <span>{timeIcon}</span>
                  {product.hoursMax === 0
                    ? product.hoursMin
                    : `${product.hoursMin}-${product.hoursMax}`}{" "}
                  {t("product-slider-learning-hours")}
                </div>
                {product.priceRange && (
                  <div>
                    <span>{priceIcon}</span> {product.priceRange}
                  </div>
                )}
              </div>
            </div>
            {product.link &&
              product.link.url &&
              (isFullUrl(product.link.url) || isAnchor(product.link.url) ? (
                <a
                  className="productSlider__slide--link modern-button modern-button-primary-outline"
                  href={product.link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {product.link.label}
                  {chevronRight}
                </a>
              ) : (
                <Link
                  className="productSlider__slide--link modern-button modern-button-primary-outline"
                  href={`/${lang}${product.link.url}`}
                >
                  {product.link.label}
                  {chevronRight}
                </Link>
              ))}
          </div>
        ))}
      </Carousel>
    </div>
  );
};

export default ProductSlider;
